/* ═══════════════════════════════════════════════════════════════════════════
   THE PAYMENT RAILS

   Four of them, and the whole point of this file is that they are NOT
   interchangeable. A screen that presents them as four logos with a toggle
   each is lying by omission: one of them cannot split a payment at all, one
   of them can only split if the account was opened in the right country, one
   of them is not a rail but a ledger sitting on top of the other two, and one
   of them is a promise to pay later rather than a payment.

   So each rail carries what it can do, what it cannot, and the sentence the
   tenant needs to read before they turn it on.

   ── Where the money goes ──────────────────────────────────────────────────

   To the business. Not to Avtale.net, not to Chamcore, not into a pooled
   account with a payout run. The keys below are the BUSINESS'S OWN merchant
   credentials, which is what makes the money theirs the moment it moves —
   there is no float sitting in somebody else's account, no payout schedule to
   argue about, and nothing for this platform to lose. It also means a tenant
   who leaves takes their merchant relationship with them, which is the
   correct amount of lock-in: none.

   The consequence is that the keys are per business and not per platform, and
   that a rail is only as configured as the least-configured tenant.

   ── Secrets ───────────────────────────────────────────────────────────────

   A client secret, an API key and a subscription key are bearer credentials:
   whoever holds one can take money in that merchant's name. This build has no
   backend and no vault, so it does not keep them. Not in localStorage — which
   any script on the page can read and which survives in a browser profile
   that other people use — and not in a JS object that a crash reporter might
   serialise.

   What is kept is whether a secret has been SET, and nothing else. The form
   can then say "a secret is stored" or "no secret yet" truthfully, the tenant
   can see what is missing, and nothing here becomes the place a real merchant
   credential leaks from. `isSecret` below is what enforces it, and
   `stripSecrets` is the function every writer has to go through.
   ═══════════════════════════════════════════════════════════════════════ */

/** Test first, always. A rail that has never taken a test payment going
    straight to live is how a tenant's first real customer is also their first
    failed payment. */
export const MODES = ['test', 'live'];

/* ── The fields ───────────────────────────────────────────────────────────
   `secret: true` means the value never leaves the form. Everything else is
   an identifier, and identifiers are not credentials: a merchant serial
   number on its own buys nobody anything. */
const F = (id, label, opts = {}) => ({ id, label, ...opts });

export const RAILS = [
  {
    id: 'vipps',
    name: 'Vipps',
    badge: '1:1',
    mark: 'V',
    lead: 'Direct 1:1 payments, and registration or sign-in for the customer. '
        + 'No split payments.',
    /* The limitation, stated on the card rather than discovered in month
       three. Vipps pays the merchant; it has no concept of a share for
       anybody else, so a revenue split has to happen outside it. */
    caveat: {
      title: 'Vipps: no split payments',
      body: 'Payment is routed in full to the business. Any share for somebody '
          + 'else is settled outside Vipps — the platform exports a CSV for '
          + 'reconciliation and does not touch the money.',
    },
    fields: [
      F('client_id', 'Client ID'),
      F('client_secret', 'Client secret', { secret: true }),
      F('subscription_key', 'Primary subscription key', { secret: true }),
      F('msn', 'Merchant serial number', { hint: 'Six digits, from the Vipps portal.' }),
      F('test_phone', 'Test phone number', {
        hint: 'Only used in test mode, and only to send the push somewhere.',
        modes: ['test'],
      }),
    ],
    /* What the booking screen may do with it once it is configured. */
    can: { push: true, split: false, identify: true, refund: true },
  },
  {
    id: 'stripe',
    name: 'Stripe',
    badge: 'Split',
    mark: '▭',
    lead: 'Split payments and routing to more than one recipient. Klarna too, '
        + 'if it is switched on in Stripe.',
    caveat: {
      title: 'Split payments depend on the account',
      body: 'Whether a payment can be split, and to whom, depends on how the '
          + 'Stripe account was opened and in which country. Read Stripe’s own '
          + 'terms for connected accounts before promising anybody a share.',
      link: ['Stripe’s terms for connected accounts', 'https://stripe.com/connect-account/legal'],
    },
    fields: [
      F('api_key', 'Stripe API key', { secret: true }),
      F('pub_key', 'Stripe publishable key', {
        hint: 'Starts with pk_. It is public by design — it is in the page.',
      }),
    ],
    can: { push: false, split: true, identify: false, refund: true },
  },
  {
    id: 'wallet',
    name: 'Wallet',
    badge: 'Internal',
    mark: '≡',
    lead: 'A prepaid balance the business sells and the business owes. It '
        + 'rides on the Vipps or Stripe rails above — it is a ledger, not a '
        + 'rail of its own.',
    caveat: {
      title: 'Wallet: the business settles it',
      body: 'A wallet purchase is routed in full to the business, and the '
          + 'business owes the treatments or the value behind it. The platform '
          + 'tracks balances and use and exports a CSV. It never holds the '
          + 'money and never funds the balance — a credit one business gives '
          + 'is that business’s to honour, not a platform-wide currency.',
    },
    /* Not a text field between them. What a wallet holds and how it is
       settled are two closed choices, and a free-text box would produce
       thirty-four different spellings of the same two answers. */
    choices: [
      {
        id: 'holds', label: 'What it holds',
        options: [
          ['block', 'A block of the same appointment',
            'Ten cuts, paid once. The balance is in appointments.'],
          ['credit', 'A balance in kroner',
            'Paid in advance and drawn down. The balance is money.'],
          ['gift', 'A gift card bought for somebody else',
            'Bought by one person, spent by another.'],
        ],
      },
      {
        id: 'settlement', label: 'Settlement',
        options: [
          ['full', 'Route it all to the business',
            'The money arrives whole. The business owes what it sold.'],
          ['csv', 'Reconcile by CSV',
            'The platform exports what was sold and what was used.'],
        ],
      },
    ],
    fields: [],
    can: { push: false, split: false, identify: false, refund: false },
  },
  {
    id: 'invoice',
    name: 'Invoice',
    badge: 'Later',
    mark: '§',
    lead: 'An invoice with a due date, sent by email and chased. Through '
        + 'Stripe, or from the business’s own bank account.',
    caveat: {
      title: 'Invoice: the business settles it',
      body: 'An invoice is a claim, not a payment. It is the business’s claim, '
          + 'against the business’s bank account, and the business is the one '
          + 'who chases it. The platform writes it, sends it, and reminds.',
    },
    fields: [
      F('account', 'Bank account', {
        hint: 'The business’s own account. Eleven digits, or an IBAN.',
      }),
      F('due_days', 'Due date', { unit: 'days after sending', number: true }),
      F('remind_after', 'Reminder sent after', { unit: 'days overdue', number: true }),
      F('first_number', 'First invoice number', {
        number: true,
        hint: 'Numbering has to be unbroken and rising — bokføringsloven. '
            + 'It can be set once, before the first invoice.',
      }),
      F('email_body', 'Email message', { long: true }),
      F('remind_body', 'Reminder message', { long: true }),
      F('copy_to', 'Send a copy to', { hint: 'The business’s own address.' }),
    ],
    can: { push: false, split: true, identify: false, refund: false },
  },
];

export const rail = id => RAILS.find(r => r.id === id) || null;

/** Every field of a rail that is a bearer credential. */
export const secretFields = id =>
  (rail(id)?.fields || []).filter(f => f.secret).map(f => f.id);

export const isSecret = (railId, field) => secretFields(railId).includes(field);

/**
 * Everything that may be written down, and nothing that may not.
 *
 * Returns `{ keep, set }` — the values safe to store, and the ids of the
 * secrets the tenant supplied. The secrets themselves are in neither. Every
 * writer goes through this; it is the only reason a merchant credential
 * cannot end up in localStorage.
 */
export function stripSecrets(railId, values) {
  const secrets = secretFields(railId);
  const keep = {};
  const set = [];
  Object.entries(values || {}).forEach(([k, v]) => {
    if (!secrets.includes(k)) { keep[k] = v; return; }
    if (String(v || '').trim()) set.push(k);
  });
  return { keep, set };
}

/** Which fields apply in the mode chosen. A test phone number in live mode is
    a field that can only be filled in wrongly. */
export const fieldsFor = (railId, mode) =>
  (rail(railId)?.fields || []).filter(f => !f.modes || f.modes.includes(mode));

/* ── Validation ───────────────────────────────────────────────────────────
   Shape only. Whether a key actually works is a question for the rail, and
   this build cannot ask it — see `verdict` below, which says so rather than
   printing a green tick it has not earned. */

const DIGITS = v => String(v || '').replace(/\s/g, '');

export function problems(railId, values, mode = 'test') {
  const out = [];
  const v = f => String(values?.[f] ?? '').trim();

  if (railId === 'vipps') {
    if (v('msn') && !/^\d{6,7}$/.test(DIGITS(v('msn'))))
      out.push(['msn', 'A merchant serial number is six or seven digits.']);
    if (mode === 'test' && v('test_phone') && !/^(\+?47)?\d{8}$/.test(DIGITS(v('test_phone'))))
      out.push(['test_phone', 'That is not a Norwegian mobile number.']);
  }

  if (railId === 'stripe') {
    const p = v('pub_key');
    if (p && !/^pk_(test|live)_/.test(p))
      out.push(['pub_key', 'A publishable key starts with pk_test_ or pk_live_.']);
    /* The one that saves somebody a bad afternoon: keys carry their own mode,
       and a test key on a live rail takes no money while looking configured. */
    if (p && mode === 'live' && p.startsWith('pk_test_'))
      out.push(['pub_key', 'That is a test key and the rail is set to live.']);
    if (p && mode === 'test' && p.startsWith('pk_live_'))
      out.push(['pub_key', 'That is a live key and the rail is set to test. '
                         + 'Real cards would be charged.']);
  }

  if (railId === 'invoice') {
    const a = DIGITS(v('account'));
    if (a && !(/^\d{11}$/.test(a) || /^NO\d{13}$/i.test(a)))
      out.push(['account', 'A Norwegian account number is eleven digits, '
                         + 'or an IBAN of the form NO plus thirteen digits.']);
    const n = num(values?.due_days);
    if (values?.due_days !== '' && values?.due_days != null && (n == null || n < 1 || n > 180))
      out.push(['due_days', 'A due date is between 1 and 180 days.']);
    const r = num(values?.remind_after);
    if (values?.remind_after !== '' && values?.remind_after != null && (r == null || r < 0 || r > 90))
      out.push(['remind_after', 'A reminder goes out between 0 and 90 days after the due date.']);
    const f = num(values?.first_number);
    if (values?.first_number !== '' && values?.first_number != null && (f == null || f < 1))
      out.push(['first_number', 'An invoice number is a positive whole number.']);
    if (v('copy_to') && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v('copy_to')))
      out.push(['copy_to', 'That does not look like an email address.']);
  }

  return out;
}

function num(v) {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) && Number.isInteger(n) ? n : null;
}

/** Is there enough here for the rail to be used at all? Missing identifiers
    are named; a secret counts as present when the tenant has set one. */
export function missing(railId, values, set = [], mode = 'test') {
  const have = f => (secretFields(railId).includes(f)
    ? set.includes(f)
    : !!String(values?.[f] ?? '').trim());
  return fieldsFor(railId, mode)
    .filter(f => !f.optional && !have(f.id))
    .map(f => f.id);
}

/**
 * What the platform is entitled to say about a rail's configuration.
 *
 * Never "verified". Shape is all this build can check, and a green tick that
 * means "the format looks right" next to one that would mean "the rail
 * accepted these keys" is the same tick doing two jobs. The day there is a
 * backend, `live` becomes a real answer from the rail and this returns it.
 */
export function verdict(railId, values, set = [], mode = 'test') {
  const bad = problems(railId, values, mode);
  const gaps = missing(railId, values, set, mode);
  if (bad.length) return { state: 'wrong', say: bad[0][1] };
  if (gaps.length) {
    const names = gaps
      .map(id => (rail(railId).fields.find(f => f.id === id) || {}).label)
      .filter(Boolean);
    return {
      state: 'part',
      say: names.length === 1
        ? `${names[0]} is still missing.`
        : `${names.length} fields are still missing: ${names.join(', ').toLowerCase()}.`,
    };
  }
  return {
    state: 'shape',
    say: 'Everything is filled in and the shapes are right. Whether the rail '
       + 'accepts these keys can only be answered by the rail, and this build '
       + 'has nothing to ask it with.',
  };
}
